"use client";
/**
 *  canvas做了如下事情
 * 1.渲染图片
 * 2.对图片根据不同的type绘制线条，渲染钉子图
 *  */
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "@/src/i18n/client";
import stl from "../styles/sizeConversionCanvas.module.scss";
import classnames from "classnames";
import { FmButton } from "@/ui-component";
import { defaultImg } from "@/config/constConf";
const nailSrc =
    "https://df5apg8r0m634.cloudfront.net/images/aa3d0fbfc9bd30ae39f3de8c9fcdb50f.png";

export interface ISizeConversionCanvasData {
    type: "X" | "Y";
    src: string;
    dimensions_bridge: number | undefined;
    dimensions_diagonal: number | undefined;
    dimensions_height: number | undefined;
    dimensions_lens: number | undefined;
    dimensions_temple: number | undefined;
    dimensions_total_width: number | undefined;
}

interface IProps {
    canvasData: ISizeConversionCanvasData;
    isHideInchInCaontainer?: boolean;
    isInches?: boolean;
    onChangeShowType?: (isInches: boolean) => void;
}

const ISINCHESBASE = 25.4;
const TOPPAINTBASENUM = 30; //顶部绘画基数
const Dev = 40; //图片缩小固定尺寸
const SizeConversionCanvas = forwardRef(function SizeConversionCanvas(
    props: IProps,
    ref,
) {
    const { onChangeShowType, isInches } = props;
    const [isInchesState, setIsInchesState] = useState(isInches);
    useEffect(() => {
        setIsInchesState(isInches);
    }, [isInches]);
    const { t } = useTranslation("productDetail");
    const [canvasSize, setCanvasSize] = useState({
        width: 0,
        height: 0,
    });
    const { canvasData, isHideInchInCaontainer } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null); // 存储画布
    const imgRef = useRef<HTMLImageElement | null>(null);
    const nailImgRef = useRef<HTMLImageElement | null>(null);

    const drawAPolygonStroke = (
        coordinate: Array<{ x: number; y: number }>,
        t: string,
        color?: string,
    ) => {
        //数组 [{x:1, y:2}, [{x:1, y:2}] 画线和多边形
        if (!(coordinate && ctxRef.current)) {
            return;
        }
        ctxRef.current.beginPath(); //开始
        let i = 0;
        while (i < coordinate.length) {
            if (i === 0) {
                ctxRef.current.moveTo(coordinate[i].x, coordinate[i].y); //落笔
            } else {
                ctxRef.current.lineTo(coordinate[i].x, coordinate[i].y); //画
            }
            i++;
        }
        ctxRef.current.lineWidth = 1; //设置线宽状态
        if (t === "Polygon") {
            //画型
            ctxRef.current.fillStyle = color ?? "#333333";
            ctxRef.current.fill();
        } //染色
        if (t === "Stroke") {
            //画线
            ctxRef.current.strokeStyle = color ?? "#333333";
            ctxRef.current.stroke();
        } //染色
        ctxRef.current.closePath(); //结束
    };
    const drawAStraightLine = useCallback((data: Record<string, any>) => {
        //画线
        if (!data) {
            return;
        }
        const c = 8;
        const t = data.stroke[0]; //起点
        const b = data.stroke[data.stroke.length - 1]; //终点
        const LStrke = []; //起点线
        const RStrke = []; //终点线
        const LTriangle = []; //起点三角
        const RTriangle = []; //终点三角
        if (data.type === 0) {
            LStrke.push({ x: t.x - 7, y: t.y - c });
            LStrke.push({ x: t.x - 7, y: t.y + c });
            RStrke.push({ x: b.x + 7, y: b.y - c });
            RStrke.push({ x: b.x + 7, y: b.y + c });

            LTriangle.push({ x: t.x - 6, y: t.y });
            LTriangle.push({ x: t.x + c - 5, y: t.y - c / 2 });
            LTriangle.push({ x: t.x + c - 5, y: t.y + c / 2 });
            RTriangle.push({ x: b.x + 6, y: b.y });
            RTriangle.push({ x: b.x - c + 5, y: b.y - c / 2 });
            RTriangle.push({ x: b.x - c + 5, y: b.y + c / 2 });
        } else {
            LStrke.push({ x: t.x - c, y: t.y - 7 });
            LStrke.push({ x: t.x + c, y: t.y - 7 });
            RStrke.push({ x: b.x - c, y: b.y + 7 });
            RStrke.push({ x: b.x + c, y: b.y + 7 });

            LTriangle.push({ x: t.x, y: t.y - 6 });
            LTriangle.push({ x: t.x - c / 2, y: t.y + c - 5 });
            LTriangle.push({ x: t.x + c / 2, y: t.y + c - 5 });
            RTriangle.push({ x: b.x, y: b.y + 6 });
            RTriangle.push({ x: b.x - c / 2, y: b.y - c + 5 });
            RTriangle.push({ x: b.x + c / 2, y: b.y - c + 5 });
        }

        drawAPolygonStroke(LStrke, "Stroke"); //左侧端点
        drawAPolygonStroke(LTriangle, "Polygon"); //左侧三角
        drawAPolygonStroke(data.stroke, "Stroke"); //最长的
        drawAPolygonStroke(RStrke, "Stroke"); //右侧端点
        drawAPolygonStroke(RTriangle, "Polygon"); //右侧三角

        //标注
        if (data.mm && ctxRef.current) {
            // ctxRef.current.font = `normal ${10 * (imgRef.current.width / 580)}px Chinese Quote`;
            ctxRef.current.font = `normal 20px Chinese Quote`; //字体大小。。。
            //开始测量字体的宽度
            const w = ctxRef.current.measureText(data.mm).width;
            // const h = ctxRef.current.measureText(data.mm).height; //开始测量字体的宽度
            let textX = 0;
            let textY = 0;
            if (data.type === 0) {
                textX = t.x + (b.x - t.x - w) / 2;
                textY = t.y - 12;
            } else {
                textX = t.x + 8;
                textY = t.y + (b.y - t.y) / 2;
            }
            ctxRef.current.fillText(data.mm, textX, textY);
        }
    }, []);

    const drawAAraw = ({ x, y }: { x: number; y: number }) => {
        // 绘制钉子
        const img = nailImgRef.current; //图片Dom
        if (!(img && imgRef.current && ctxRef.current)) {
            return;
        }
        const W = img.width; //钉子 宽度
        const height = img.height; //钉子 高度
        const pcV = imgRef.current.width / 580;

        ctxRef.current.drawImage(img, x, y, W * pcV, height * pcV);
    };
    // 副作用：对 isInchesState, width, height的变动做更新
    const effectOfImgOnLoadP = useCallback(
        (width: number, height: number, img: HTMLImageElement) => {
            const aspectRatio = width / height; //宽高比例
            const canvasWidth = width - Dev * 2; //图片再canvas 中绘制出来的宽度
            const canvasHeight = canvasWidth / aspectRatio; //图片再canvas 中绘制出来的高度
            const Company = isInchesState ? "inch" : "mm"; //单位
            const coefficient = isInchesState ? ISINCHESBASE : 1;
            const pcV = width / 580;
            const dimensions_total_width = {
                stroke: [
                    { x: 65 * pcV, y: height - 50 * pcV },
                    { x: width - 65 * pcV, y: height - 50 * pcV },
                ],
                mm:
                    (canvasData.dimensions_total_width! / coefficient).toFixed(
                        Company === "mm" ? 0 : 2,
                    ) + Company,
                begin: "left",
                close: "right",
                type: 0,
            }; //线
            const dimensions_temple = {
                stroke: [
                    {
                        x: (width * 0.3 * 100) / canvasData.dimensions_temple!,
                        y: height * 0.1,
                    },
                    { x: width * 0.75, y: height * 0.1 },
                    { x: width * 0.9, y: height * 0.3 },
                ],
                mm:
                    (canvasData.dimensions_temple! / coefficient).toFixed(
                        Company === "mm" ? 0 : 2,
                    ) + Company,
                begin: "left",
                close: "right",
                type: 0,
            }; //线
            const dimensions_lens = {
                stroke: [
                    { x: width / 2 + 50 * pcV, y: TOPPAINTBASENUM * pcV },
                    { x: width - 40 - 40 * pcV, y: TOPPAINTBASENUM * pcV },
                ],
                mm:
                    (canvasData.dimensions_lens! / coefficient).toFixed(
                        Company === "mm" ? 0 : 2,
                    ) + Company,
                begin: "left",
                close: "right",
                type: 0,
            }; //线

            const dimensions_height = {
                stroke: [
                    { x: 15 + (15 * pcV) / 2, y: 80 * pcV },
                    { x: 15 + (15 * pcV) / 2, y: height - 80 * pcV },
                ],
                mm:
                    (canvasData.dimensions_height! / coefficient).toFixed(
                        Company === "mm" ? 0 : 2,
                    ) + Company,
                begin: "left",
                close: "right",
                type: 1,
            }; //线

            const dimensions_bridge = {
                stroke: [
                    { x: width / 2 - 25 * pcV, y: TOPPAINTBASENUM * pcV },
                    { x: width / 2 + 25 * pcV, y: TOPPAINTBASENUM * pcV },
                ],
                mm:
                    (canvasData.dimensions_bridge! / coefficient).toFixed(
                        Company === "mm" ? 0 : 2,
                    ) + Company,
                begin: "left",
                close: "right",
                type: 0,
            }; //线
            if (!(ctxRef.current && nailImgRef.current)) {
                return;
            }
            // onSizeLoading();
            ctxRef.current.translate(0, 0); //设置中心为左上角
            ctxRef.current.clearRect(-1000, -1000, 2000, 2000); //清空画布

            ctxRef.current.drawImage(
                img,
                Dev,
                Dev / aspectRatio,
                canvasWidth,
                canvasHeight,
            );
            if (canvasData.type === "Y") {
                drawAPolygonStroke(
                    [
                        dimensions_total_width.stroke[0],
                        {
                            x:
                                dimensions_total_width.stroke[0].x +
                                10 +
                                nailImgRef.current.width / 2,
                            y:
                                dimensions_total_width.stroke[0].y +
                                10 +
                                nailImgRef.current.height / 2,
                        },
                    ],
                    "Stroke",
                    "#ccc",
                );
                drawAPolygonStroke(
                    [
                        dimensions_total_width.stroke[1],
                        {
                            x:
                                dimensions_total_width.stroke[1].x -
                                10 -
                                nailImgRef.current.width / 2,
                            y:
                                dimensions_total_width.stroke[1].y +
                                10 +
                                nailImgRef.current.height / 2,
                        },
                    ],
                    "Stroke",
                    "#ccc",
                );
                ctxRef.current.translate(10 * pcV, 10 * pcV); //位移
                drawAAraw(dimensions_total_width.stroke[0]);
                ctxRef.current.translate(-10 * pcV, -10 * pcV); //还原
                ctxRef.current.translate(
                    -10 * pcV - nailImgRef.current.height,
                    10 * pcV,
                ); //位移
                drawAAraw(dimensions_total_width.stroke[1]);
                ctxRef.current.translate(
                    10 * pcV + nailImgRef.current.height,
                    -10 * pcV,
                ); //还原
                drawAStraightLine(dimensions_total_width);
                drawAStraightLine(dimensions_lens);
                drawAStraightLine(dimensions_bridge);
                drawAStraightLine(dimensions_height);
            } else if (canvasData.type === "X") {
                drawAPolygonStroke(
                    [
                        dimensions_temple.stroke[0],
                        {
                            x:
                                dimensions_temple.stroke[0].x -
                                nailImgRef.current.width,
                            y: dimensions_temple.stroke[0].y - 10,
                        },
                    ],
                    "Stroke",
                    "#ccc",
                );
                ctxRef.current.translate(
                    -30 * pcV - nailImgRef.current.height,
                    -26 * pcV,
                ); //位移
                drawAAraw(dimensions_temple.stroke[0]);
                ctxRef.current.translate(
                    30 * pcV + nailImgRef.current.height,
                    26 * pcV,
                ); //还原
                drawAStraightLine(dimensions_temple);
            }
        },
        [
            canvasData.dimensions_bridge,
            canvasData.dimensions_height,
            canvasData.dimensions_lens,
            canvasData.dimensions_temple,
            canvasData.dimensions_total_width,
            canvasData.type,
            drawAStraightLine,
            isInchesState,
        ],
    );
    useEffect(() => {
        if (!imgRef.current) {
            return;
        }
        effectOfImgOnLoadP(canvasSize.width, canvasSize.height, imgRef.current); // 当前方法执行的副作用改动
    }, [canvasSize.height, canvasSize.width, effectOfImgOnLoadP]);

    // componentDidMount: 用canvas绘制图片，图片执行以后做绘制线条和钉子图
    useEffect(() => {
        // 获取canvas元素的引用
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        // 获取绘图上下文
        ctxRef.current = canvas.getContext("2d");
        // 创建一个新的Image对象
        imgRef.current = new Image();
        nailImgRef.current = new Image();
        imgRef.current.crossOrigin = "anonymous";
        nailImgRef.current.crossOrigin = "anonymous";
        // 设置图片的来源（src）
        imgRef.current.src = canvasData.src; // 替换为你的图片路径
        nailImgRef.current.src = nailSrc;
        imgRef.current.onload = () => {
            setCanvasSize({
                width: imgRef.current?.width ?? 1,
                height: imgRef.current?.height ?? 1,
            });
        };
        imgRef.current.onerror = () => {
            if (imgRef.current) {
                imgRef.current.src = defaultImg;
            }
        };
        return () => {
            if (imgRef.current) {
                imgRef.current.onload = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handle change canvas show inch or mm
    const handleChangeCanvasInch = useCallback(
        (isInchesState: boolean) => {
            onChangeShowType?.(isInchesState);
        },
        [onChangeShowType],
    );

    return (
        <div className={stl["size-canvas-container"]}>
            <canvas
                ref={canvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                className={stl["canvas-container"]}
            ></canvas>
            <FmButton
                className={classnames(stl["canvas-inch"], {
                    ["hidden"]: isHideInchInCaontainer,
                })}
                variant={"text"}
                onClick={() => handleChangeCanvasInch(!isInchesState)}
            >
                {isInchesState
                    ? t("productDetail:ShowInMm")
                    : t("productDetail:ShowInInches")}
            </FmButton>
        </div>
    );
});

export default SizeConversionCanvas;
